import { computed, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import Unipile from "@/services/unipile/unipile";
import type { EmailConversationsParams } from "./types";

function isRef<T>(param: Ref<T> | T): param is Ref<T> {
  return typeof param === "object" && param !== null && "value" in param;
}

/**
 * Composable for fetching email conversations using TanStack Query.
 * Fetches every (account, contact email) pair in parallel and merges the results,
 * so a reply that landed in any of the user's connected inboxes shows up — the
 * Buckets table's unread-email badge counts across all of them, so the list must too.
 * Each item is tagged with `accountId` so follow-up calls (open thread, mark read,
 * reply, delete) go to the account that actually owns it.
 */
export function useEmailConversations(
  params: Ref<EmailConversationsParams | null> | EmailConversationsParams | null
) {
  const queryKey = computed(() => {
    const p = isRef(params) ? params.value : params;
    if (!p) return ["email-conversations", "disabled"];
    const sortedAccounts = [...p.accountIds].sort();
    const sortedEmails = [...p.contactEmails].sort();
    return ["email-conversations", sortedAccounts.join(","), ...sortedEmails];
  });

  const enabled = computed(() => {
    const p = isRef(params) ? params.value : params;
    return !!p && p.accountIds.length > 0 && p.contactEmails.length > 0;
  });

  return useQuery({
    queryKey,
    queryFn: async () => {
      const p = isRef(params) ? params.value : params;
      if (!p) throw new Error("Parameters not ready");

      const requests = p.accountIds.flatMap((accountId) =>
        p.contactEmails.map((email) => ({ accountId, email }))
      );

      // Single request: let errors surface. Multiple: one failing account/address
      // shouldn't blank out the whole list.
      const responses = await Promise.all(
        requests.map(({ accountId, email }) =>
          Unipile.getAllEmails({ account_id: accountId, limit: p.limit || 50, any_email: email })
            .then((res) => ({ accountId, res }))
            .catch((error) => {
              if (requests.length === 1) throw error;
              return null;
            })
        )
      );

      // Deduplicate by account + ID (IDs are only unique within an account)
      const emailsArray: any[] = [];
      const seen = new Set<string>();
      for (const entry of responses) {
        if (!entry) continue;
        const { accountId, res } = entry;
        const items = Array.isArray(res.data.data) ? res.data.data : res.data.data?.items || [];
        for (const item of items) {
          const key = `${accountId}:${item.id}`;
          if (!seen.has(key)) {
            seen.add(key);
            emailsArray.push({ ...item, accountId });
          }
        }
      }

      // Sort by date descending
      emailsArray.sort((a, b) => {
        const dateA = new Date(a.thread_metadata?.last_message_date || a.date || 0).getTime();
        const dateB = new Date(b.thread_metadata?.last_message_date || b.date || 0).getTime();
        return dateB - dateA;
      });

      // Transform to conversation list format
      return emailsArray.map((email) => {
        const lastMessage = email.subject && email.subject.trim() ? email.subject : "(No subject)";

        const displayName =
          email.from_attendee?.display_name || email.from_attendee?.identifier || "Unknown";

        const unreadCount =
          email.is_thread && email.thread_metadata
            ? email.thread_metadata.unread_count || 0
            : email.read_date
              ? 0
              : 1;

        const threadCount =
          email.is_thread && email.thread_metadata ? email.thread_metadata.total_emails || 1 : 1;

        const isRead =
          email.is_thread && email.thread_metadata
            ? (email.thread_metadata.unread_count || 0) === 0
            : !!email.read_date;

        const subject = email.subject && email.subject.trim() ? email.subject : "(No subject)";
        const bodyPreview = email.body_plain
          ? email.body_plain.substring(0, 120).trim()
          : email.snippet || "";
        const fromEmail = email.from_attendee?.identifier || "";

        const emailRole = email.role?.toLowerCase();
        const isOutbound =
          emailRole === "from" ||
          emailRole === "sent" ||
          (email.folders &&
            Array.isArray(email.folders) &&
            email.folders.some((f: string) => f.toLowerCase().includes("sent")));

        let emailStatus = "sent";
        if (isOutbound) {
          // Outbound: "read" only when recipient opened via tracking pixel
          const hasTrackingEvents =
            (email.trackingOpenCount || 0) > 0 || (email.trackingClickCount || 0) > 0;
          if (hasTrackingEvents) {
            emailStatus = "read";
          }
        } else {
          // Inbound: "read" when we viewed it (IMAP flag)
          if (
            email.read_date ||
            (email.thread_metadata &&
              email.thread_metadata.unread_count === 0 &&
              email.thread_metadata.total_emails > 0)
          ) {
            emailStatus = "read";
          }
        }
        if (emailStatus === "sent" && email.delivery_status === "delivered") {
          emailStatus = "delivered";
        }

        return {
          ...email,
          id: email.id,
          title: displayName,
          displayName,
          subject,
          lastMessage: bodyPreview || lastMessage,
          lastMessageAt:
            email.thread_metadata?.last_message_date || email.date || new Date().toISOString(),
          contactName: displayName,
          contactEmail: fromEmail,
          isRead,
          unreadCount,
          threadCount,
          status: emailStatus,
        };
      });
    },
    enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}
